import Error from "@mui/icons-material/Error";
import HourglassBottom from "@mui/icons-material/HourglassBottom";
import PauseCircleFilled from "@mui/icons-material/PauseCircleFilled";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import {
  GridColDef,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid";
import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import PageContainer from "../components/Common/PageContainer";
import SectionTable, { NoRowsOverlay } from "../components/Common/SectionTable";
import DashboardLayout from "../components/DashboardLayout/DashboardLayout";
import InternalError from "../components/Errors/InternalError";
import Loading from "../components/Loading";
import { useAssets } from "../components/Orion/OrionQueries";
import AssetListingHeader from "../components/PageHeader/AssetListing";
import useSession from "../components/useSession/useSession";
import { AssetAttrs } from "../interfaces/api";
import { Status } from "../interfaces/common";

export const AssetsListingPage: React.FC = () => {
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [sortFields, setsortFields] = useState<GridSortModel | undefined>([
    { field: "updated_date", sort: "desc" },
  ]);

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      setsortFields(
        sortModel.map((model) => ({
          field: model.field,
          sort: model.sort,
        }))
      );
    },
    []
  );
  const navigate = useNavigate();
  const {
    data: paginatedAssets,
    isLoading: isLoadingAssets,
    isError: isErrorAssets,
  } = useAssets({
    page: pageNumber,
    limit: pageSize,
    sortFields: sortFields,
  });
  const assets = paginatedAssets?.items;
  const { data: session } = useSession();

  function getRowId(row: AssetAttrs) {
    return row._id;
  }

  const columns: GridColDef<AssetAttrs>[] = [
    {
      field: "updated_date",
      headerName: "Date",
      minWidth: 200,
      flex: 0.5,
      type: "dateTime",
      valueGetter: (value, row) => new Date(row.updated_date),
    },
    {
      field: "name",
      headerName: "Nom",
      minWidth: 200,
      flex: 2,
      cellClassName: "font-bold",
    },
    {
      field: "processing.config_char.name",
      headerName: "Configuration",
      sortable: false,
      minWidth: 200,
      flex: 2,
      cellClassName: "no-underline text-[#4c4fa2] font-semibold",
      valueGetter: (value, row) => row.processing?.config_char?.name,
      renderCell: (params) =>
        params.row.processing?.config_char &&
        (session?.OrionContributorGroups.includes(
          params.row.processing.config_char._id
        ) ||
          session?.OrionManagerGroups.includes(
            params.row.processing.config_char._id
          )) ? (
          <Link
            to={`/orion/config/${params.row.processing.config_char._id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {params.value}
          </Link>
        ) : (
          params.value
        ),
    },
    {
      field: "workspace",
      headerName: "Espace de travail",
      sortable: false,
      minWidth: 200,
      flex: 1,
      valueGetter: (value, row) => {
        const workspace = session?.workspaces.find(
          (workspace) => workspace._id === row.workspace_id
        );
        if (workspace) {
          return workspace.name;
        }
      },
    },
    {
      field: "status",
      headerAlign: "center",
      align: "center",
      sortable: false,
      headerName: "Statut",
      minWidth: 120,
      flex: 1,
      valueGetter: (value, row) =>
        row.processing?.status === Status.DONE
          ? row.processing?.workflow
          : row.processing?.status,
      renderCell: (params) => {
        if (params.value === Status.PENDING) {
          return (
            <Tooltip title="Traitement en attente">
              <PauseCircleFilled
                color="warning"
                sx={{ verticalAlign: "middle" }}
              />
            </Tooltip>
          );
        } else if (params.value === Status.IN_PROGRESS) {
          return (
            <Tooltip title="Traitement en cours">
              <HourglassBottom color="info" sx={{ verticalAlign: "middle" }} />
            </Tooltip>
          );
        } else if (params.value === Status.ERROR) {
          return (
            <Tooltip title="Une erreur est survenue">
              <Error color="error" sx={{ verticalAlign: "middle" }} />
            </Tooltip>
          );
        } else {
          return <Chip label={params.value} color={"default"} />;
        }
      },
    },
  ];

  const HeaderComponent = useCallback(
    () =>
      assets && <AssetListingHeader selection={selection} assets={assets} />,
    [selection, assets]
  );
  return (
    <DashboardLayout>
      <PageContainer slots={{ header: HeaderComponent }} className="max-w-full">
        {isErrorAssets ? (
          <InternalError />
        ) : isLoadingAssets ? (
          <Loading />
        ) : (
          <SectionTable<AssetAttrs>
            rows={assets ?? []}
            dataGridProps={{
              slots: {
                noRowsOverlay: () => NoRowsOverlay("Aucun dossier"),
              },
              checkboxSelection: true,
              getRowId: getRowId,
              columns,
              onRowClick: (params) =>
                navigate(`/orion/asset/${params.row._id}`),
            }}
            setSelection={setSelection}
            paginationProps={
              paginatedAssets && {
                totalRows: paginatedAssets.total_count,
                pageNumber,
                setPageNumber,
                pageSize,
                setPageSize,
                handleSortModelChange,
              }
            }
          />
        )}
      </PageContainer>
    </DashboardLayout>
  );
};
